import { useCallback, useState, useEffect } from 'react';
import { FormGroup, Level, LevelItem, Button, TextInput, Modal, ModalVariant, Divider, DropdownItem, Dropdown, MenuToggle, MenuToggleElement } from '@patternfly/react-core';
import { ArrowRightIcon, EllipsisVIcon } from '@patternfly/react-icons';
import { useTranslation } from 'react-i18next';

import ModelSelectionRuleWrapper from './SelectionRuleWrapper';
import DebouncedTextInput from '../../components/debounced-text-input';
import { ModelSelectionUndoState } from './ModelSelectionPage';
import { AeTitleRule, CustomizationObjectType, ModelSelectionScope, ModelType } from '../../store/global-types/customization-types';
import { BackendValidationErrorViewModel, FormValidationError } from '../../store/global-types/store-errors';
import ValidationHelperText from '../../components/validation-helper-text';
import { getFormValidationErrorMessage } from '../../store/global-types/form-errors';

interface AeTitleRuleFormProps {
    modelType: ModelType,
    prefix: string,
    aeTitleRule: AeTitleRule,
    validationError: BackendValidationErrorViewModel | undefined,
    formValidationErrors?: FormValidationError[],
    runningNumber: number,
    isLastRule: boolean,
    setUndoState: (undoState: ModelSelectionUndoState) => void,
    isUndoDisabled?: boolean,
    onAeTitleRuleActionUpdated: (action: string) => void,
    onAeTitleRuleRemoved: () => void,
}

const generateAeTitle = (action: string, prefix: string): string => `${prefix}${action.toUpperCase()}`;

const AeTitleRuleForm = (props: AeTitleRuleFormProps) => {
    const { modelType, prefix, aeTitleRule, validationError, formValidationErrors = [], runningNumber, setUndoState, isUndoDisabled, onAeTitleRuleActionUpdated, onAeTitleRuleRemoved, isLastRule } = props;

    const { t } = useTranslation();

    const [isActionListOpen, setActionListOpenState] = useState(false);
    const [isRemoveAeTitleRuleModalOpen, setRemoveAeTitleRuleModalOpenState] = useState(false);


    const [actionFieldValidation, setActionFieldValidation] = useState<{ validated: 'default' | 'error', helperTextInvalid?: string }>({ validated: 'default' });

    /** Set non-blocking form validation errors generated in redux store reducers to be visible in this form's normal UI validation. */
    useEffect(() => {
        if (formValidationErrors.length > 0) {
            setActionFieldValidation({ 
                validated: 'error', 
                helperTextInvalid: formValidationErrors.map((e: FormValidationError) => getFormValidationErrorMessage(e.errorType)).join(' ') 
            });
        } else {
            setActionFieldValidation({ validated: 'default' });
        }
    }, [formValidationErrors]);

    const handleAeTitleActionChange = useCallback((action: string) => {
        onAeTitleRuleActionUpdated(action);
        // dispatch(aeTitleRuleActionUpdated({ id: aeTitleRuleId, action }));
    }, [onAeTitleRuleActionUpdated]);

    const handleRemoveAeTitleRuleButtonClick = useCallback(() => {
        setActionListOpenState(false);
        setRemoveAeTitleRuleModalOpenState(true);
    }, []);

    const handleRemoveAeTitleRuleConfirmation = useCallback(() => {
        setRemoveAeTitleRuleModalOpenState(false);
        onAeTitleRuleRemoved();
        // dispatch(aeTitleRuleRemoved(aeTitleRuleId));
    }, [onAeTitleRuleRemoved]);

    const handleUndo = useCallback(() => {
        setUndoState({ isModalOpen: true, scope: ModelSelectionScope.AeTitleRule, id: aeTitleRule.id });
        setActionListOpenState(false)
    }, [aeTitleRule, setUndoState]);

    const fieldId = aeTitleRule.id;
    const formValidationState = actionFieldValidation.validated;

    const dropdownItems = [<DropdownItem
        key="removeAeTitleRule" isDisabled={!aeTitleRule.isEditable}
        title={aeTitleRule.isEditable ? undefined : t('selectionPage.defaultNamesCannotBeRemoved')}
        onClick={handleRemoveAeTitleRuleButtonClick}>{t('selectionPage.removeAeTitleRule')}</DropdownItem>];
    if (!isUndoDisabled) {
        dropdownItems.unshift(<DropdownItem key="undoAeTitleRuleChanges" isDisabled={!aeTitleRule.isModified || aeTitleRule.isNew}
            onClick={handleUndo}>{t('selectionPage.undoAeTitleRuleChanges')}</DropdownItem>);
    }

    const hasValidationError = validationError !== undefined && validationError.type === CustomizationObjectType.AeTitleRule;

    // render either an ae title field or a dicom attribute field
    return (
        <ModelSelectionRuleWrapper
            key={aeTitleRule.id}
            title={t('selectionPage.aeTitleRuleNumber', { number: runningNumber })}
            modelType={modelType}
            isModified={aeTitleRule.isModified}
            className="ae-title-rule"
            hasValidationError={hasValidationError}
            validationError={validationError}
            testId="ae-title-rule">

            <div className="rule-item">
                <Level className="ae-title-row">
                    <LevelItem className="ae-title-action-column">
                        <FormGroup
                            label={t('selectionPage.actionName')}
                            fieldId={`action-name-field-for-${fieldId}`}
                            title={`The action name that can be used to access this model. AE Title is generated from this field.${aeTitleRule.isEditable ? '' : ' Default action names/AE Titles cannot be modified, but you can add new ones.'}`}
                        >
                            <DebouncedTextInput
                                fieldId={`action-name-field-for-${fieldId}`}
                                onDebouncedChange={handleAeTitleActionChange}
                                className="ae-title"
                                defaultValue={aeTitleRule.action}
                                isDisabled={!aeTitleRule.isEditable}
                                validated={formValidationState}
                                testId="ae-title-action-input"
                            />
                            {formValidationState !== 'default' && (
                                <ValidationHelperText
                                    validated={formValidationState}
                                    helperText={actionFieldValidation.helperTextInvalid}
                                />
                            )}
                        </FormGroup>
                    </LevelItem>
                    <LevelItem className="ae-title-arrow">
                        <ArrowRightIcon />
                    </LevelItem>
                    <LevelItem className="ae-title-aetitle-column">
                        <FormGroup
                            label={t('selectionPage.generatedAeTitle')}
                            fieldId={`ae-title-field-for-${fieldId}`}
                            title="AE Title is generated from the action name. AE Titles cannot be changed directly -- adjust the action name instead."
                        >
                            <TextInput
                                className="ae-title"
                                type="text"
                                id={`ae-title-field-for-${fieldId}`}
                                value={generateAeTitle(aeTitleRule.action, prefix)}
                                isDisabled
                            />
                        </FormGroup>
                    </LevelItem>
                    <LevelItem style={{ alignItems: 'bottom' }}>
                        <Dropdown
                            toggle={(toggleRef: React.Ref<MenuToggleElement>) => <MenuToggle ref={toggleRef} onClick={() => setActionListOpenState(!isActionListOpen)} variant='plain'><EllipsisVIcon /></MenuToggle>}
                            isPlain
                            popperProps={{ position: 'right' }}
                            isOpen={isActionListOpen}
                            shouldFocusFirstItemOnOpen={false}
                            onOpenChange={() => setActionListOpenState(!isActionListOpen)}
                        >{dropdownItems}</Dropdown>
                    </LevelItem>
                </Level>
            </div>
            <Modal
                variant={ModalVariant.small}
                title={t('selectionPage.confirmRemoveAeTitleRule')}
                isOpen={isRemoveAeTitleRuleModalOpen}
                onClose={() => setRemoveAeTitleRuleModalOpenState(false)}
                actions={[
                    <Button key="confirmRemoval" variant="danger" onClick={handleRemoveAeTitleRuleConfirmation}>{t('selectionPage.removeAeTitleRule')}</Button>,
                    <Button key="cancel" variant="tertiary" onClick={() => setRemoveAeTitleRuleModalOpenState(false)}>{t('common.cancel')}</Button>
                ]}
            >
                {/* eslint-disable-next-line react/no-unescaped-entities */}
                Do you want to remove this entire AE Title rule ("{generateAeTitle(aeTitleRule.action, prefix)}")? Note that your changes won't be saved until you click on "Save".
            </Modal>
            {!isLastRule && (<Divider className="selection-divider" />)}
        </ModelSelectionRuleWrapper>
    );
}

export default AeTitleRuleForm;
